/**
 * Plays the sonar mosaic window: lays the mosaic down fan by fan as the drone moves
 * along its track, then holds the finished product. Razor renders the window, its
 * panels and copy; this fills the stage and reports progress through `data-state`
 * on the root and a few `data-sonar-mosaic-*` hooks.
 *
 * The build and drawing are ported from Blueye Cloud's landing mockup
 * (`web/src/pages/landing/mockups/sonar-mosaic-mockup.tsx` at 370414e0).
 */

import { mountBasemap } from "../shared/basemap";
import { prefersReducedMotion, whenInView } from "../shared/in-view";
import { placeMapOverlay } from "../shared/map-stage";
import { COPPER } from "../shared/palette";
import { createFieldReveal, playOver, type PreparedField } from "../shared/playback";
import { runOnce } from "../shared/worker";
import {
    FAN_HALF_APERTURE_RAD,
    FAN_RANGE_M,
    FIELD_HEIGHT,
    FIELD_WIDTH,
    FRAME_HEIGHT_M,
    FRAME_WIDTH_M,
    colourField,
    getMosaicField,
    type MosaicField,
    type TrackSample,
} from "./field";

/** Centre of the survey frame: the open basin between two piers in Trondheim harbour. */
const FRAME_CENTER: [number, number] = [10.4201, 63.4435];
/** Esri has no zoom 19 imagery of the harbour; asking for it costs a round of failed tiles. */
const IMAGERY_MAX_ZOOM = 18;
/** Share of the stage width left on each side of the frame, so the quays around it show. */
const CONTEXT_MARGIN = 0.14;
/** Display-canvas pixels per metre — twice the field, so the track and fan stay crisp. */
const DISPLAY_SCALE = 8;
const BUILD_DURATION_MS = 7000;
/** Blueye cyan, which stays legible over every part of the copper ramp. */
const TRACK_RGB = "0, 216, 210";
const SCALE_BAR_M = 20;

type MosaicState = "idle" | "building" | "saved";

interface MosaicElements {
    root: HTMLElement;
    stage: HTMLElement;
    basemap: HTMLElement;
    canvas: HTMLCanvasElement;
    progress: HTMLElement | null;
    replay: HTMLButtonElement | null;
}

type PreparedMosaic = PreparedField<MosaicField>;

export function mountSonarMosaic(root: HTMLElement): void {
    const elements = findElements(root);
    if (!elements) {
        return;
    }
    const layout = placeMapOverlay(root, elements.stage, elements.canvas, {
        frameWidthM: FRAME_WIDTH_M,
        frameHeightM: FRAME_HEIGHT_M,
        margin: CONTEXT_MARGIN,
        center: FRAME_CENTER,
        displayScale: DISPLAY_SCALE,
        scaleBarM: SCALE_BAR_M,
        palette: COPPER,
    });

    let prepared: Promise<PreparedMosaic> | null = null;
    let ready: PreparedMosaic | null = null;
    let cancel = () => {};
    const prepare = () => (prepared ??= prepareField());
    const reducedMotion = prefersReducedMotion();
    if (reducedMotion && elements.replay) {
        elements.replay.hidden = true;
    }

    const play = (animate: boolean) => {
        if (!ready) {
            return;
        }
        cancel();
        setState(elements, "building");
        cancel = playBuild(elements.canvas, ready, animate, {
            onProgress: (fraction) => setProgress(elements, fraction),
            onDone: () => setState(elements, "saved"),
        });
    };

    // Fetch the tiles and start the survey a little before the window shows, so the
    // build starts on a loaded map without a pause.
    whenInView(
        root,
        () => {
            mountBasemap(elements.basemap, FRAME_CENTER, layout, IMAGERY_MAX_ZOOM);
            void prepare();
        },
        { rootMargin: "400px 0px" },
    );
    whenInView(
        elements.stage,
        () => {
            void prepare().then((field) => {
                ready = field;
                play(!reducedMotion);
            });
        },
        { threshold: 0.35 },
    );
    elements.replay?.addEventListener("click", () => play(true));
}

/**
 * Builds the survey in a worker: millions of noise and fan samples would otherwise block
 * the main thread, and with it scrolling, as the window approaches.
 */
function prepareField(): Promise<PreparedMosaic> {
    return runOnce(
        () => new Worker(new URL("./field.worker.ts", import.meta.url), { type: "module" }),
        null,
        (): PreparedMosaic => {
            const field = getMosaicField();
            return { field, colours: colourField(field, COPPER) };
        },
    );
}

function findElements(root: HTMLElement): MosaicElements | null {
    const stage = root.querySelector<HTMLElement>("[data-sonar-mosaic-stage]");
    const basemap = root.querySelector<HTMLElement>("[data-sonar-mosaic-basemap]");
    const canvas = root.querySelector<HTMLCanvasElement>("[data-sonar-mosaic-canvas]");
    if (!stage || !basemap || !canvas) {
        return null;
    }
    return {
        root,
        stage,
        basemap,
        canvas,
        progress: root.querySelector<HTMLElement>("[data-sonar-mosaic-progress]"),
        replay: root.querySelector<HTMLButtonElement>("[data-sonar-mosaic-replay]"),
    };
}

function setState(elements: MosaicElements, state: MosaicState): void {
    elements.root.dataset.state = state;
    if (state === "saved") {
        setProgress(elements, 1);
    }
}

function setProgress(elements: MosaicElements, fraction: number): void {
    if (elements.progress) {
        elements.progress.style.width = `${Math.round(fraction * 100)}%`;
    }
}

/**
 * Reveals the field sample by sample over `BUILD_DURATION_MS`, or all at once without
 * `animate`. Returns a function that stops a build still in progress, for a replay.
 */
function playBuild(
    canvas: HTMLCanvasElement,
    { field, colours }: PreparedMosaic,
    animate: boolean,
    callbacks: { onProgress: (fraction: number) => void; onDone: () => void },
): () => void {
    const context = canvas.getContext("2d");
    const reveal = createFieldReveal(FIELD_WIDTH, FIELD_HEIGHT, colours, field);
    if (!context || !reveal) {
        return () => {};
    }
    const lastSample = field.samples.length - 1;
    return playOver(
        BUILD_DURATION_MS,
        animate,
        (fraction) => {
            const sample = Math.round(fraction * lastSample);
            reveal.through(sample);
            drawFrame(context, reveal.canvas, field.samples, sample, fraction < 1);
            callbacks.onProgress(fraction);
        },
        callbacks.onDone,
    );
}

function drawFrame(
    context: CanvasRenderingContext2D,
    fieldCanvas: HTMLCanvasElement,
    samples: readonly TrackSample[],
    upTo: number,
    showFan: boolean,
): void {
    const { width, height } = context.canvas;
    context.clearRect(0, 0, width, height);
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = "high";
    context.drawImage(fieldCanvas, 0, 0, width, height);

    context.lineJoin = "round";
    context.lineCap = "round";
    context.strokeStyle = `rgba(${TRACK_RGB}, 0.85)`;
    context.lineWidth = 2.5;
    context.beginPath();
    for (let i = 0; i <= upTo; i++) {
        const sample = samples[i]!;
        context.lineTo(sample.x * DISPLAY_SCALE, sample.y * DISPLAY_SCALE);
    }
    context.stroke();

    const drone = samples[upTo]!;
    const x = drone.x * DISPLAY_SCALE;
    const y = drone.y * DISPLAY_SCALE;
    if (showFan) {
        const heading = Math.atan2(drone.hy, drone.hx);
        context.beginPath();
        context.moveTo(x, y);
        context.arc(
            x,
            y,
            FAN_RANGE_M * DISPLAY_SCALE,
            heading - FAN_HALF_APERTURE_RAD,
            heading + FAN_HALF_APERTURE_RAD,
        );
        context.closePath();
        context.fillStyle = `rgba(${TRACK_RGB}, 0.12)`;
        context.fill();
        context.strokeStyle = `rgba(${TRACK_RGB}, 0.75)`;
        context.lineWidth = 2;
        context.stroke();
    }

    context.beginPath();
    context.arc(x, y, 7, 0, Math.PI * 2);
    context.fillStyle = "#ffffff";
    context.shadowColor = `rgba(${TRACK_RGB}, 0.9)`;
    context.shadowBlur = 10;
    context.fill();
    context.shadowBlur = 0;
}
